import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { CalendarDays, Clock, ArrowRight } from "lucide-react";
import { api, fileUrl, inr } from "@/lib/api";
import { Seo } from "@/components/site/Seo";

const CAT_COLOR = {
  treks: "bg-emerald-500", "dham-yatras": "bg-amber-500", trips: "bg-primary",
  "weekend-getaways": "bg-sky-500", adventure: "bg-rose-500", "custom-group": "bg-violet-500",
};

export default function CalendarPage() {
  const [deps, setDeps] = useState([]);

  useEffect(() => { api.get("/departures").then(({ data }) => setDeps(data)); }, []);

  const grouped = useMemo(() => {
    const g = {};
    deps.forEach((d) => {
      const dt = new Date(d.date);
      const key = dt.toLocaleString("en", { month: "long", year: "numeric" });
      (g[key] = g[key] || []).push(d);
    });
    return g;
  }, [deps]);

  return (
    <div className="pt-16">
      <Seo title="Departure Calendar" description="All upcoming departure dates with prices — pick a date and book your seat." />
      <div className="bg-secondary text-secondary-foreground">
        <div className="mx-auto max-w-7xl px-6 py-16">
          <p className="uppercase tracking-[0.25em] text-gold text-sm">Plan ahead</p>
          <h1 className="font-heading text-5xl sm:text-6xl font-semibold mt-2">Departure Calendar</h1>
          <p className="text-secondary-foreground/70 mt-3 max-w-xl">Every upcoming departure at a glance. Tap any date to view the trip, price and duration.</p>
          <div className="mt-6 flex flex-wrap gap-3 text-xs">
            {Object.entries({ Treks: "treks", "Dham Yatras": "dham-yatras", Trips: "trips", "Weekend": "weekend-getaways", Adventure: "adventure" }).map(([label, k]) => (
              <span key={k} className="flex items-center gap-1.5"><span className={`h-2.5 w-2.5 rounded-full ${CAT_COLOR[k]}`} /> {label}</span>
            ))}
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-7xl px-6 py-12 pb-24">
        {Object.keys(grouped).length === 0 && <p className="text-center text-muted-foreground py-20 font-heading text-3xl">No upcoming departures yet.</p>}
        {Object.entries(grouped).map(([month, items]) => (
          <div key={month} className="mb-12">
            <h2 className="font-heading text-3xl mb-5 sticky top-16 bg-background/90 glass py-2 z-10">{month}</h2>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {items.map((d, i) => {
                const dt = new Date(d.date);
                return (
                  <motion.div key={d.id} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: (i % 3) * 0.05 }}>
                    <Link to={`/trips/${d.trip_slug}`} data-testid="calendar-departure"
                      className="group flex overflow-hidden rounded-2xl border border-border bg-card hover:shadow-xl hover:-translate-y-1 transition-transform duration-300">
                      <div className="w-24 shrink-0 relative">
                        <img src={fileUrl(d.cover_image)} alt="" className="h-full w-full object-cover" loading="lazy" />
                        <span className={`absolute top-0 left-0 h-1.5 w-full ${CAT_COLOR[d.category] || "bg-primary"}`} />
                      </div>
                      <div className="flex-1 p-4">
                        <div className="flex items-center gap-1.5 text-primary font-mono text-sm">
                          <CalendarDays className="h-4 w-4" /> {dt.getDate()} {dt.toLocaleString("en", { month: "short" }).toUpperCase()}
                        </div>
                        <h3 className="font-heading text-xl leading-tight mt-1 group-hover:text-primary transition-colors">{d.trip_name}</h3>
                        <div className="mt-2 flex items-center justify-between">
                          <span className="text-xs text-muted-foreground flex items-center gap-1"><Clock className="h-3.5 w-3.5" /> {d.duration}</span>
                          <span className="font-heading text-2xl text-primary"><span className="text-xs font-sans text-muted-foreground mr-1">from</span>{inr(d.from_price)}</span>
                        </div>
                        <span className={`mt-1 inline-flex items-center gap-1 text-xs font-medium ${d.sold_out ? "text-destructive" : d.seats_left != null && d.seats_left <= 5 ? "text-amber-600 dark:text-amber-400" : "text-emerald-600 dark:text-emerald-400"}`} data-testid="calendar-seat-status">
                          {d.sold_out ? "Sold Out" : d.seats_left != null && d.seats_left <= 5 ? `Only ${d.seats_left} seat${d.seats_left === 1 ? "" : "s"} left` : d.status} <ArrowRight className="h-3 w-3 group-hover:translate-x-1 transition-transform" />
                        </span>
                      </div>
                    </Link>
                  </motion.div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
